import { Paper, Stack, Typography, useTheme, alpha } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { TrafficLight } from "./cells/TrafficLight";
import {
  PRICE_DIFF_GREEN_THRESHOLD,
  PRICE_DIFF_YELLOW_THRESHOLD,
} from "./cells/PriceComparisonCell";

const LEGEND_ITEMS = ["success" as const, "warning" as const, "error" as const];

const formatThreshold = (
  color: "success" | "warning" | "error",
  unit: string
): string => {
  switch (color) {
    case "success":
      return `≤ ${PRICE_DIFF_GREEN_THRESHOLD} ${unit}`;
    case "warning":
      return `${PRICE_DIFF_GREEN_THRESHOLD}–${PRICE_DIFF_YELLOW_THRESHOLD} ${unit}`;
    case "error":
      return `${PRICE_DIFF_YELLOW_THRESHOLD}+ ${unit}`;
  }
};

export const ComplianceLegendBox = () => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const units = useUnitsFormatter(false);
  const unit = units("specific_cost", false);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: alpha(theme.palette.info.main, 0.04),
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
        borderRadius: 2,
        alignSelf: "flex-start",
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: theme.palette.info.main }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: "fontWeightSemiBold",
              color: theme.palette.text.primary,
            }}
          >
            {t("complianceLegendDescription")}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2}>
          {LEGEND_ITEMS.map((color) => (
            <Stack
              key={color}
              direction="row"
              spacing={0.75}
              alignItems="center"
            >
              <TrafficLight color={color} size="1rem" />
              <Typography variant="body2">
                {formatThreshold(color, unit)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};
